import React, { useState, useEffect } from 'react';
import Modal from '@components/ui/Modal';
import { RiMapPinLine, RiSearchLine } from '@remixicon/react';
import { useAirportSearch } from '../services/airportService';

/**
 * QuoteSearchByAirportModal
 * Type-ahead picker backed by the BE `search=` param. On confirm, calls
 * onSelect(icaoCode, label) where label is e.g. "KOPF — Miami-Opa Locka Exec".
 * Requires min 2 chars before hitting the server.
 */
const QuoteSearchByAirportModal = ({ isOpen, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  // Debounce the input 300ms so we don't fire a request per keystroke.
  // Separate from the query state so the input stays responsive.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);
  const { data: results = [], isLoading } = useAirportSearch(debouncedQuery);

  const pickAirport = (airport) => {
    const code = airport.icao || airport.code;
    if (!code) return;
    const cityPart = airport.city ? ` — ${airport.city}` : (airport.name ? ` — ${airport.name}` : '');
    onSelect(code, `${code}${cityPart}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Search by Airport" size="md">
      <div className="p-6 space-y-4">
        <div className="relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search ICAO / IATA / city / name (e.g. KOPF or Miami)"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>

        <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg">
          {isLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading airports…</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              {query ? 'No airports match your search.' : 'Start typing to search.'}
            </p>
          ) : (
            <ul>
              {results.map((airport) => {
                const code = airport.icao || airport.code;
                return (
                  <li key={`${code}-${airport.name || ''}`}>
                    <button
                      type="button"
                      onClick={() => pickAirport(airport)}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <RiMapPinLine className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900">{code}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {airport.name || ''}{airport.city ? ` · ${airport.city}` : ''}{airport.country ? `, ${airport.country}` : ''}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteSearchByAirportModal;
